import { tileWidth, tileHeight } from '../data/tileset.config';
import { entitiesConfig } from '../data/entities.config';
import { emotionEffectiveness } from '../data/emotions.config';
import {
  DirectionEnum,
  ActionPhase,
  LagType,
  DungeonEntityType,
  Element,
} from '../types/enums';
import { mapService } from '../services/MapService';
import { dungeonService } from '../services/DungeonService';
import {
  isTileBlocking,
  getDirectionFromPositions,
  getTargetPosition,
} from '../utils/map.utils';
import { lerp } from '../utils/general.utils';
import { v4 as uuidv4 } from 'uuid';
import { diagonalAdjusted, msToTicks } from '../utils/timing.utils';
import {
  ActionDefinition,
  ICreateAffectedTileOptions,
  ISpriteClip,
} from '../types/types';
import { animationService } from '../services/AnimationService';
import {
  sendDamageNumber,
  sendPreviewAdd,
  sendPreviewRemove,
} from '../net/combatEvents';
import { netService } from '../services/NetService';
import {
  DungeonEntityProps,
  IActionTimings,
  IDamageDisplay,
  IHealDisplay,
  IEntityStats,
  DEFAULT_ENTITY_STATS,
} from '../types/entity.types';
import { ISpellAnimation } from '../types/animation.types';
import { ActiveBuff } from '../types/buff.types';
import { tickBuffs, emitEntityEvent } from '../utils/buff.utils';
import { BUFF_REGISTRY } from '../data/buffs.config';
import { SPELL_REGISTRY } from '../data/spells.config';
import { checkEffectConditions } from '../effect-system/conditionChecker';
import { EFFECT_HANDLERS } from '../effect-system/effectHandlers';
import { SpellState } from '../types/spell.types';
import type { SpellDefinition } from '../types/spell.types';
import type { BuffDefinition } from '../types/buff.types';
import { getMasteryLevelLive } from '../services/SpellMasteryService';

export type {
  DungeonEntityProps,
  IActionTimings,
  IDamageDisplay,
  IHealDisplay,
};

// Client cooldown reconciliation deadband (~100 ms) — below this, the predicted
// cooldown is trusted so RTT jitter doesn't make the HUD bar stutter.
const COOLDOWN_RECONCILE_TICKS = msToTicks(100);

// One knockback step = one cell, animated like a move with a mid-step
// collision check (cf. `move()`).
const KNOCKBACK_STEP_MS = 200;
const KNOCKBACK_BLOCKED_STUN_MS = 600;

// Balance constants — cf. docs/stats.md

/** Floor for `spellCastTime` after bonuses are applied. */
const CAST_TIME_MULTIPLIER_FLOOR = 0.05;

// Exponent applied to the attack/defense ratio to soften it (1 = raw ratio,
// 0 = stats ignored → always ×1). Calibrated so a 2:1 ratio yields ×1.5 (and a
// 1:2 ratio ×0.667). Symmetric, so high defense keeps reducing toward 0 with no
// floor — and, symmetrically, zero attack deals zero.
const STAT_RATIO_EXPONENT = Math.log2(1.5); // ≈ 0.585

// Re-entrance guard for thorns reflect — prevents reflect ↔ reflect loops.
let isReflecting = false;

/**
 * `key` is a scalar stat key (`'physicalAttack'`). The dotted-path branch
 * below is legacy — no Record-typed stat remains on `IEntityStats`.
 */
function readStatValue(stats: IEntityStats, key: string): number {
  const dotIdx = key.indexOf('.');
  if (dotIdx < 0) {
    return (stats as unknown as Record<string, number>)[key];
  }
  const recordKey = key.slice(0, dotIdx);
  const elemKey = key.slice(dotIdx + 1);
  const record = (stats as unknown as Record<string, Record<string, number>>)[
    recordKey
  ];
  return record?.[elemKey] ?? 0;
}

function writeStatValue(stats: IEntityStats, key: string, value: number): void {
  const dotIdx = key.indexOf('.');
  if (dotIdx < 0) {
    (stats as unknown as Record<string, number>)[key] = value;
    return;
  }
  const recordKey = key.slice(0, dotIdx);
  const elemKey = key.slice(dotIdx + 1);
  const record = (stats as unknown as Record<string, Record<string, number>>)[
    recordKey
  ];
  if (record) {
    record[elemKey] = value;
  }
}

interface IProgressCallback {
  percent: number; // 0-100
  callback: () => void;
  triggered?: boolean; // guards against firing more than once
}

interface IEntityAction {
  name: string;
  phase: ActionPhase;
  phaseElapsedTime: number;
  timings: IActionTimings;
  castProgressCallbacks?: IProgressCallback[];
  actionProgressCallbacks?: IProgressCallback[];
  lagProgressCallbacks?: IProgressCallback[];
}

interface IVisualMovement {
  startX: number;
  startY: number;
  targetX: number;
  targetY: number;
  elapsedTime: number;
  duration: number;
  easing?: (t: number) => number;
  onComplete?: () => void;
}

export abstract class DungeonEntity {
  id: string;
  type: DungeonEntityType;
  life: number;
  maxLife: number;
  shield: number;
  maxShield: number;
  stats: IEntityStats;
  public baseStats: IEntityStats;
  isWalkableOver: boolean;
  targetable: boolean;
  knockbackable: boolean;
  configId: string;
  level: number;
  positionX: number;
  positionY: number;
  absoluteX = 0;
  absoluteY = 0;
  animated = true;
  currentSpriteName = 'idle';
  direction: DirectionEnum = DirectionEnum.BOTTOM;

  public isDirty = true;
  public renderVersion = 0;

  // Host: last input sequence processed for this (player) entity. Serialized in
  // snapshots so a client only reconciles its predicted position once the host
  // has caught up to its inputs. 0 for monsters / unprocessed.
  public lastInputSeq = 0;

  // Per-player input gate: false while this player is mid blocking-action.
  // Per-entity (not a global on dungeonService) so co-op players don't block
  // each other's input. The global floor-transition gate is separate.
  public allowInput = true;

  protected actionState: IEntityAction | null = null;
  protected visualMovement: IVisualMovement | null = null;
  protected idleTickCounter = 0;
  protected damageDisplays: IDamageDisplay[] = [];
  protected healDisplays: IHealDisplay[] = [];
  protected affectedTileIds: string[] = [];
  public currentCastGroupId: string | null = null;
  protected spellAnimationIds: string[] = [];

  public lastDamageTakenAt: number = Date.now();

  /**
   * Set by the `dealDamage`/`dealDamageInCells` handlers when `ctx.spell` is
   * defined. Read on death to propagate `killSourceSpellId` into the killer's
   * `onKill` hook.
   */
  public lastDamageSourceSpellId?: string;
  /** Last caster (id) to deal damage — used to fire `onKill`. */
  public lastDamageSourceCasterId?: string;

  protected activeBuffs: ActiveBuff[] = [];

  protected spellStates: Map<string, SpellState> = new Map();

  /** Run-scoped per-spell clones, mutated by THIS entity's sockets/trinkets at
   *  build (cf. buildPlayerRunEffects). Players only; monsters cast from the
   *  static registry. */
  public runSpellInstances = new Map<string, SpellDefinition>();
  public runBuffInstances = new Map<string, BuffDefinition>();
  /** Set on remote players from the synced snapshot; undefined → read the live
   *  catalog store (local player / solo). */
  public masteryLevels: Record<string, number> | undefined = undefined;

  constructor(props: DungeonEntityProps) {
    this.id = uuidv4();
    this.type = props.type;
    this.baseStats = { ...DEFAULT_ENTITY_STATS, ...props.stats };
    this.stats = { ...this.baseStats };
    this.maxLife = props.maxLife ?? this.stats.maxLife;
    this.life = this.maxLife;
    this.maxShield = this.stats.maxShield;
    this.shield = props.shield ?? this.maxShield;
    this.isWalkableOver = props.isWalkableOver;
    this.targetable = props.targetable ?? true;
    this.knockbackable = props.knockbackable ?? true;
    this.configId = props.configId;
    this.level = props.level ?? 1;
    this.positionX = props.x;
    this.positionY = props.y;
    this.absoluteX = props.x * tileWidth;
    this.absoluteY = props.y * tileHeight;
  }

  public tick(deltaTime: number): void {
    this.updateCurrentAction(deltaTime);
    tickBuffs(this, deltaTime);
    this.recomputeStats();
    this.tickCooldowns(deltaTime);

    if (!this.actionState) {
      this.idleTickCounter++;
    } else {
      this.idleTickCounter = 0;
    }

    this.updateVisualMovement(deltaTime);
  }

  /**
   * Client-side prediction tick for the locally-controlled player: advance the
   * current action, the visual slide, and the predicted cooldowns. Stats and
   * buffs stay host-authoritative, so this skips `recomputeStats`/`tickBuffs` —
   * running them would recompute from the client's own (wrong) baseStats and
   * fight the snapshot values. Cooldowns are predicted locally on cast
   * (`predictSpellCooldown`) and corrected by `reconcileCooldowns` from snapshots.
   */
  public tickPrediction(deltaTime: number): void {
    this.updateCurrentAction(deltaTime);
    this.tickCooldowns(deltaTime);
    // Drives the return-to-idle sprite once an action ends (cf. tick()); without
    // it the predicted player stays stuck on the 'run' clip after a move.
    if (!this.actionState) {
      this.idleTickCounter++;
    } else {
      this.idleTickCounter = 0;
    }
    this.updateVisualMovement(deltaTime);
  }

  private tickCooldowns(deltaTime: number): void {
    for (const state of Array.from(this.spellStates.values())) {
      if (state.cooldownRemaining > 0) {
        state.cooldownRemaining -= deltaTime;
      }
    }
  }

  public addBuff(buff: ActiveBuff): void {
    this.activeBuffs.push(buff);
    this.markDirty();
    if (buff.def.onApply) {
      const ctx = {
        caster: this,
        direction: 0,
        affectedCells: [],
      };
      for (const step of buff.def.onApply) {
        const handler = EFFECT_HANDLERS[step.handler];
        if (handler) {
          handler(ctx, { ...step.params });
        }
      }
    }
  }

  /** Refreshes if already stunned, and cancels any in-flight cast. */
  public applyStun(durationTicks: number): void {
    const def = BUFF_REGISTRY['stunned'];
    if (!def) {
      return;
    }
    const existing = this.activeBuffs.find((b) => b.def.id === 'stunned');
    if (existing) {
      existing.remainingTicks = durationTicks;
    } else {
      this.addBuff({ def, remainingTicks: durationTicks });
    }
    this.cancelCast();
  }

  public removeBuff(buff: ActiveBuff): void {
    const idx = this.activeBuffs.indexOf(buff);
    if (idx !== -1) {
      this.activeBuffs.splice(idx, 1);
      this.markDirty();
    }
  }

  public getActiveBuffs(): ActiveBuff[] {
    return this.activeBuffs;
  }

  /**
   * Client-only: replace the active buffs with the host-replicated set (for
   * display — icons + size/opacity). A wholesale replace, so a buff the host
   * dropped (antidote, dispel, expiry) is cleared here. Bypasses `addBuff` (no
   * `onApply`/hooks — those are host-authoritative). When `recompute`, derive
   * `stats` from the new buffs (size/opacity) but restore the host's HP/shield:
   * `recomputeStats` would recompute them from the client's own baseStats, which
   * lack the host's elite/run scaling.
   */
  public setReplicatedBuffs(buffs: ActiveBuff[], recompute: boolean): void {
    this.activeBuffs = buffs;
    this.markDirty();
    if (!recompute) {
      return;
    }
    const { life, maxLife, shield, maxShield } = this;
    this.recomputeStats();
    this.life = life;
    this.maxLife = maxLife;
    this.shield = shield;
    this.maxShield = maxShield;
  }

  /**
   * Called every tick. This is the only place `stats` is written after
   * construction.
   */
  public recomputeStats(): void {
    const computed: IEntityStats = { ...this.baseStats };

    const flatBonus: Record<string, number> = {};
    const percentBonus: Record<string, number> = {};

    for (const activeBuff of this.activeBuffs) {
      for (const mod of activeBuff.def.modifiers ?? []) {
        if (mod.conditions && !checkEffectConditions(this, mod.conditions)) {
          continue;
        }
        const key = mod.stat as string;
        if (mod.amount !== undefined) {
          flatBonus[key] = (flatBonus[key] ?? 0) + mod.amount;
        }
        if (mod.percent !== undefined) {
          percentBonus[key] = (percentBonus[key] ?? 100) + mod.percent;
        }
      }
    }

    const touched = new Set([
      ...Object.keys(flatBonus),
      ...Object.keys(percentBonus),
    ]);
    for (const key of touched) {
      const flat = flatBonus[key] ?? 0;
      const pct = percentBonus[key] ?? 100;
      const base = readStatValue(this.baseStats, key);
      writeStatValue(computed, key, ((base + flat) * pct) / 100);
    }

    computed.critChancePhysical = Math.min(100, computed.critChancePhysical);
    computed.critChanceMagic = Math.min(100, computed.critChanceMagic);
    computed.spellCastTime = Math.max(
      CAST_TIME_MULTIPLIER_FLOOR,
      computed.spellCastTime,
    );

    this.stats = computed;

    // `maxLife` / `maxShield` are the source of truth for the entity's actual
    // life/shield (HP bar, damage, death), but their value is driven by the
    // stat pipeline — without this sync, a +maxLife buff/talent/boon would land
    // in `stats.maxLife` and never raise the real cap. Raising the cap also
    // grants the difference as current resource (taking a +maxLife talent heals
    // by that amount, and a fresh run starts full); a lowered cap clamps down.
    const maxLifeDelta = computed.maxLife - this.maxLife;
    this.maxLife = computed.maxLife;
    if (maxLifeDelta > 0) {
      this.life += maxLifeDelta;
    }
    if (this.life > this.maxLife) {
      this.life = this.maxLife;
    }

    const maxShieldDelta = computed.maxShield - this.maxShield;
    this.maxShield = computed.maxShield;
    if (maxShieldDelta > 0) {
      this.shield += maxShieldDelta;
    }
    if (this.shield > this.maxShield) {
      this.shield = this.maxShield;
    }
  }

  ensureSpellInstance(spellId: string): SpellDefinition | undefined {
    const existing = this.runSpellInstances.get(spellId);
    if (existing) {
      return existing;
    }
    const base = SPELL_REGISTRY[spellId];
    if (!base) {
      return undefined;
    }
    const clone = JSON.parse(JSON.stringify(base)) as SpellDefinition;
    this.runSpellInstances.set(spellId, clone);
    return clone;
  }

  getSpellInstance(spellId: string): SpellDefinition | undefined {
    return this.runSpellInstances.get(spellId);
  }

  ensureBuffInstance(buffId: string): BuffDefinition | undefined {
    const existing = this.runBuffInstances.get(buffId);
    if (existing) {
      return existing;
    }
    const base = BUFF_REGISTRY[buffId];
    if (!base) {
      return undefined;
    }
    const clone = JSON.parse(JSON.stringify(base)) as BuffDefinition;
    this.runBuffInstances.set(buffId, clone);
    return clone;
  }

  getBuffInstance(buffId: string): BuffDefinition | undefined {
    return this.runBuffInstances.get(buffId);
  }

  /** Mastery level for this entity: synced map for remote players, live catalog
   *  store for the local player / solo. */
  getMasteryLevel(spellId: string): number {
    return this.masteryLevels
      ? (this.masteryLevels[spellId] ?? 1)
      : getMasteryLevelLive(spellId);
  }

  /** Reset run-scoped instances before a (re)build. */
  resetRunInstances(): void {
    this.runSpellInstances.clear();
    this.runBuffInstances.clear();
  }

  public getSpellState(spellId: string): SpellState {
    let state = this.spellStates.get(spellId);
    if (!state) {
      state = { spellId, cooldownRemaining: 0 };
      this.spellStates.set(spellId, state);
    }
    return state;
  }

  /** Only non-zero cooldowns, for the save. */
  public getSpellStatesSnapshot(): SpellState[] {
    const out: SpellState[] = [];
    for (const state of Array.from(this.spellStates.values())) {
      if (state.cooldownRemaining > 0) {
        out.push({ ...state });
      }
    }
    return out;
  }

  /** Restore from a save, replacing existing cooldown state. */
  public restoreSpellStates(states: SpellState[]): void {
    for (const s of states) {
      this.spellStates.set(s.spellId, {
        spellId: s.spellId,
        cooldownRemaining: s.cooldownRemaining,
      });
    }
  }

  /**
   * Cooldown formula in ticks (config ms → runtime ticks, scaled by the caster's
   * spell-cooldown stat). Shared by the host cast path (SpellService) and client
   * prediction. The first-cast / max-stacks SKIPS are NOT here: they consume
   * host-only run state (firstCastSkip counter) and read host-ticked buffs, so
   * they stay in SpellService.applyCooldown — see docs/multiplayer.md § Cooldowns.
   */
  public computeCooldownTicks(spellId: string, cooldownMs: number): number {
    return msToTicks(cooldownMs * this.getSpellCooldownMult(spellId));
  }

  /**
   * Client prediction: start a spell's cooldown locally on cast so the HUD bar
   * reacts immediately (the host owns the real cast). Mirrors the host formula
   * in SpellService; corrected by `reconcileCooldowns` from the next snapshot.
   */
  public predictSpellCooldown(spell: {
    id: string;
    cooldownMs?: number;
  }): void {
    if (!spell.cooldownMs) {
      return;
    }
    const state = this.getSpellState(spell.id);
    state.cooldownRemaining = this.computeCooldownTicks(
      spell.id,
      spell.cooldownMs,
    );
  }

  /**
   * Client: align predicted cooldowns to the host snapshot, but only past a
   * threshold — RTT-scale drift is left to prediction so the HUD bar stays
   * smooth; a real misprediction (e.g. a cast the host rejected) is corrected.
   * Spells absent from the snapshot are host-ready (cooldown 0).
   */
  public reconcileCooldowns(states: SpellState[]): void {
    const hostCd = new Map(states.map((s) => [s.spellId, s.cooldownRemaining]));
    for (const state of Array.from(this.spellStates.values())) {
      const host = hostCd.get(state.spellId) ?? 0;
      if (Math.abs(host - state.cooldownRemaining) > COOLDOWN_RECONCILE_TICKS) {
        state.cooldownRemaining = host;
      }
    }
    for (const s of states) {
      if (!this.spellStates.has(s.spellId)) {
        this.spellStates.set(s.spellId, {
          spellId: s.spellId,
          cooldownRemaining: s.cooldownRemaining,
        });
      }
    }
  }

  public getSpellCooldownMult(_spellId: string): number {
    return this.stats.spellCooldown;
  }

  public getSpellCastTimeMult(_spellId: string): number {
    return this.stats.spellCastTime;
  }

  /**
   * Restore active buffs from a save. The `def`s must already be resolved by
   * the caller via `BUFF_REGISTRY` (an unknown buff is dropped before here).
   */
  public restoreActiveBuffs(buffs: ActiveBuff[]): void {
    this.activeBuffs = [];
    for (const b of buffs) {
      this.activeBuffs.push(b);
    }
    this.recomputeStats();
    this.markDirty();
  }

  abstract getAvailableActions(): ActionDefinition[];

  protected updateCurrentAction(deltaTime: number): void {
    if (!this.actionState) {
      if (this.idleTickCounter > 1 && this.currentSpriteName !== 'idle') {
        this.currentSpriteName = 'idle';
        this.markDirty();
      }
      return;
    }

    this.actionState.phaseElapsedTime += deltaTime;

    this.checkProgressCallbacks();

    let phaseComplete = false;
    let nextPhase: ActionPhase | null = null;

    switch (this.actionState.phase) {
      case ActionPhase.CASTING:
        if (
          this.actionState.phaseElapsedTime >= this.actionState.timings.castTime
        ) {
          phaseComplete = true;
          nextPhase = ActionPhase.EXECUTING;
        }
        break;

      case ActionPhase.EXECUTING:
        if (
          this.actionState.phaseElapsedTime >=
          this.actionState.timings.actionTime
        ) {
          phaseComplete = true;
          nextPhase = ActionPhase.LAGGING;
        }
        break;
      case ActionPhase.LAGGING:
        if (
          this.actionState.phaseElapsedTime >= this.actionState.timings.lagTime
        ) {
          phaseComplete = true;
        }
        break;
    }

    if (phaseComplete) {
      if (nextPhase) {
        this.actionState.phase = nextPhase;
        this.actionState.phaseElapsedTime = 0;

        this.skipZeroDurationPhases();

        this.updateCurrentActionSprite();
        this.markDirty();
      } else {
        this.actionState = null;

        if (this.type === DungeonEntityType.PLAYER) {
          this.allowInput = true;
        }
      }
    }
  }

  protected skipZeroDurationPhases(): void {
    if (!this.actionState) {
      return;
    }

    let shouldContinue = true;

    while (shouldContinue && this.actionState) {
      shouldContinue = false;
      const { phase, timings } = this.actionState;

      switch (phase) {
        case ActionPhase.CASTING:
          if (timings.castTime === 0) {
            this.actionState.castProgressCallbacks?.forEach((cb) => {
              if (cb.percent === 100 && !cb.triggered) {
                cb.triggered = true;
                cb.callback();
              }
            });
            this.actionState.phase = ActionPhase.EXECUTING;
            this.actionState.phaseElapsedTime = 0;
            shouldContinue = true;
          }
          break;

        case ActionPhase.EXECUTING:
          if (timings.actionTime === 0) {
            this.actionState.actionProgressCallbacks?.forEach((cb) => {
              if (cb.percent === 100 && !cb.triggered) {
                cb.triggered = true;
                cb.callback();
              }
            });
            this.actionState.phase = ActionPhase.LAGGING;
            this.actionState.phaseElapsedTime = 0;
            shouldContinue = true;
          }
          break;

        case ActionPhase.LAGGING:
          if (timings.lagTime === 0) {
            this.actionState.lagProgressCallbacks?.forEach((cb) => {
              if (cb.percent === 100 && !cb.triggered) {
                cb.triggered = true;
                cb.callback();
              }
            });
            this.actionState = null;

            if (this.type === DungeonEntityType.PLAYER) {
              this.allowInput = true;
            }
            shouldContinue = false;
          }
          break;
      }
    }
  }

  protected checkProgressCallbacks(): void {
    if (!this.actionState) {
      return;
    }

    const { phase, phaseElapsedTime, timings } = this.actionState;
    let phaseDuration = 0;
    let callbacks: IProgressCallback[] | undefined;

    switch (phase) {
      case ActionPhase.CASTING:
        phaseDuration = timings.castTime;
        callbacks = this.actionState.castProgressCallbacks;
        break;
      case ActionPhase.EXECUTING:
        phaseDuration = timings.actionTime;
        callbacks = this.actionState.actionProgressCallbacks;
        break;
      case ActionPhase.LAGGING:
        phaseDuration = timings.lagTime;
        callbacks = this.actionState.lagProgressCallbacks;
        break;
    }

    if (!callbacks || phaseDuration === 0) {
      return;
    }

    const currentPercent = (phaseElapsedTime / phaseDuration) * 100;

    callbacks.forEach((progressCallback) => {
      if (
        !progressCallback.triggered &&
        currentPercent >= progressCallback.percent
      ) {
        progressCallback.triggered = true;
        progressCallback.callback();
      }
    });
  }

  public startAction(
    actionName: string,
    applyCastTime: boolean,
    timings: IActionTimings,
    progressCallbacks?: {
      castProgressCallbacks?: IProgressCallback[];
      actionProgressCallbacks?: IProgressCallback[];
      lagProgressCallbacks?: IProgressCallback[];
    },
    force = false,
  ): void {
    if (!force && !this.canCast()) {
      return;
    }

    // Cast-time multiplier applies to spells only (move/knockback/death pass false).
    let adjustedTimings = timings;
    if (applyCastTime) {
      const multiplier = this.getSpellCastTimeMult(actionName);
      adjustedTimings = {
        ...timings,
        castTime: timings.castTime * multiplier,
        actionTime: timings.actionTime * multiplier,
        lagTime: timings.lagTime * multiplier,
      };
    }

    this.actionState = {
      name: actionName,
      phase: ActionPhase.CASTING,
      phaseElapsedTime: 0,
      timings: adjustedTimings,
      ...progressCallbacks,
    };

    this.actionState.castProgressCallbacks?.forEach((cb) => {
      if (cb.triggered === undefined) {
        cb.triggered = false;
      }
    });
    this.actionState.actionProgressCallbacks?.forEach((cb) => {
      if (cb.triggered === undefined) {
        cb.triggered = false;
      }
    });
    this.actionState.lagProgressCallbacks?.forEach((cb) => {
      if (cb.triggered === undefined) {
        cb.triggered = false;
      }
    });

    this.skipZeroDurationPhases();

    this.updateCurrentActionSprite();
    this.markDirty();

    if (
      this.type === DungeonEntityType.PLAYER &&
      (timings.castBlocking === LagType.FULL ||
        timings.actionBlocking === LagType.FULL)
    ) {
      this.allowInput = false;
    }
  }

  protected updateCurrentActionSprite(): void {
    if (!this.actionState) {
      return;
    }

    const { phase, timings } = this.actionState;

    switch (phase) {
      case ActionPhase.CASTING:
        this.currentSpriteName = timings.castSpriteName || 'idle';
        break;
      case ActionPhase.EXECUTING:
        this.currentSpriteName = timings.actionSpriteName || 'idle';
        break;
      case ActionPhase.LAGGING:
        this.currentSpriteName = timings.lagSpriteName || 'idle';
        break;
      default:
        this.currentSpriteName = 'idle';
    }
  }

  protected updateVisualMovement(deltaTime: number): void {
    if (!this.visualMovement) {
      return;
    }

    this.visualMovement.elapsedTime += deltaTime;
    const progress = Math.min(
      1,
      this.visualMovement.elapsedTime / this.visualMovement.duration,
    );
    const easedProgress = this.visualMovement.easing?.(progress) ?? progress;

    this.absoluteX = lerp(
      this.visualMovement.startX,
      this.visualMovement.targetX,
      easedProgress,
    );
    this.absoluteY = lerp(
      this.visualMovement.startY,
      this.visualMovement.targetY,
      easedProgress,
    );

    if (progress >= 1) {
      // Clear before the callback so an onComplete that starts a new slide
      // (e.g. a step-by-step charge dash chaining via slideTo) isn't wiped.
      const onComplete = this.visualMovement.onComplete;
      this.visualMovement = null;
      onComplete?.();
    }

    this.markDirty();
  }

  protected startVisualMovement(
    targetX: number,
    targetY: number,
    duration: number,
    easing?: (t: number) => number,
    onComplete?: () => void,
  ): void {
    this.visualMovement = {
      startX: this.absoluteX,
      startY: this.absoluteY,
      targetX: targetX * tileWidth,
      targetY: targetY * tileHeight,
      elapsedTime: 0,
      duration,
      easing,
      onComplete,
    };
  }

  /**
   * Logical jump to one adjacent cell with a visual slide from the current
   * position over `durationMs` (e.g. one step of a charge dash). The logical
   * position updates at once; the sprite lerps `absoluteX/Y` toward it.
   * `onComplete` fires when the slide ends — used to chain step-by-step dashes.
   */
  public slideTo(
    targetX: number,
    targetY: number,
    durationMs: number,
    onComplete?: () => void,
  ): void {
    const duration = diagonalAdjusted(
      msToTicks(durationMs),
      this.positionX,
      this.positionY,
      targetX,
      targetY,
    );
    this.positionX = targetX;
    this.positionY = targetY;
    this.startVisualMovement(targetX, targetY, duration, undefined, onComplete);
  }

  public canCast(): boolean {
    if (this.isStunned()) {
      return false;
    }
    if (this.isDead()) {
      return false;
    }
    if (this.actionState === null) {
      return true;
    }

    const blocking = this.getCurrentBlocking();
    return blocking !== LagType.FULL && blocking !== LagType.CAST;
  }

  public isStunned(): boolean {
    for (const b of this.activeBuffs) {
      if (b.def.stuns) {
        return true;
      }
    }
    return false;
  }

  public isCasting(): boolean {
    return this.actionState?.phase === ActionPhase.CASTING;
  }

  /**
   * Aborts only if still in the CAST phase, so knockback cancels casts but
   * already-launched attacks still resolve.
   */
  public cancelCast(): boolean {
    if (!this.isCasting()) {
      return false;
    }
    const groupId = this.currentCastGroupId;
    if (groupId) {
      this.removeAffectedTilesByGroup(groupId);
      if (netService.isHost()) {
        sendPreviewRemove({ groupId });
      }
      this.currentCastGroupId = null;
    }
    this.actionState = null;
    if (this.type === DungeonEntityType.PLAYER) {
      this.allowInput = true;
    }
    this.markDirty();
    return true;
  }

  public createAffectedTile(
    x: number,
    y: number,
    options?: ICreateAffectedTileOptions,
  ): string {
    const tileId = dungeonService.previewService.createAffectedTile(x, y, {
      entityId: this.id,
      isPlayer: this.type === DungeonEntityType.PLAYER,
      ...options,
    });
    this.affectedTileIds.push(tileId);
    return tileId;
  }

  // Batched preview creation: all cells share one groupId. Host broadcasts a single
  // preview-add so clients see the telegraph (combat is host-authoritative -> clients
  // run no execute). castSeq, when set, lets the originating client reconcile its
  // predicted preview against this authoritative one.
  public createAffectedTiles(
    cells: { x: number; y: number }[],
    options?: ICreateAffectedTileOptions & { castSeq?: number },
  ): string {
    const { castSeq, ...tileOptions } = options ?? {};
    const groupId = tileOptions.groupId ?? uuidv4();
    for (const cell of cells) {
      this.createAffectedTile(cell.x, cell.y, { ...tileOptions, groupId });
    }
    if (netService.isHost()) {
      sendPreviewAdd({
        groupId,
        entityId: this.id,
        cells,
        duration: tileOptions.duration ?? 0,
        isPlayer: this.type === DungeonEntityType.PLAYER,
        castSeq,
        color: tileOptions.color,
        alpha: tileOptions.alpha,
        previewType: tileOptions.type,
        noProgressFill: tileOptions.noProgressFill,
      });
    }
    return groupId;
  }

  protected createRelativeAffectedTiles(
    relativeTiles: { x: number; y: number }[],
    options?: ICreateAffectedTileOptions,
  ): string[] {
    return relativeTiles.map((tile) =>
      this.createAffectedTile(
        this.positionX + tile.x,
        this.positionY + tile.y,
        options,
      ),
    );
  }

  protected removeAffectedTile(tileId: string): void {
    dungeonService.previewService.removeAffectedTile(tileId);
    const index = this.affectedTileIds.indexOf(tileId);
    if (index !== -1) {
      this.affectedTileIds.splice(index, 1);
    }
  }

  protected clearAffectedTiles(): void {
    this.affectedTileIds.forEach((id) =>
      dungeonService.previewService.removeAffectedTile(id),
    );
    this.affectedTileIds = [];
  }

  protected removeAffectedTilesByGroup(groupId: string): void {
    dungeonService.previewService.removeAffectedTilesByGroup(groupId);
    this.affectedTileIds = this.affectedTileIds.filter((id) =>
      dungeonService.previewService.getAffectedTiles().some((t) => t.id === id),
    );
  }

  public getAffectedTileIds(): string[] {
    return this.affectedTileIds;
  }

  protected createSpellAnimation(
    x: number,
    y: number,
    spriteName: string,
    duration: number,
    options?: {
      direction?: DirectionEnum;
      isLooping?: boolean;
      animationSpeed?: number | 'auto';
      onComplete?: () => void;
    },
  ): string {
    const animationId = animationService.createAnimation(
      spriteName,
      x,
      y,
      duration,
      {
        ...options,
        originId: this.id,
      },
    );
    this.spellAnimationIds.push(animationId);
    return animationId;
  }

  protected removeSpellAnimation(animationId: string): void {
    animationService.removeSpellAnimation(animationId);
    const index = this.spellAnimationIds.indexOf(animationId);
    if (index !== -1) {
      this.spellAnimationIds.splice(index, 1);
    }
  }

  protected clearSpellAnimations(): void {
    this.spellAnimationIds.forEach((id) =>
      animationService.removeSpellAnimation(id),
    );
    this.spellAnimationIds = [];
  }

  public getSpellAnimations(): ISpellAnimation[] {
    return animationService.getSpellAnimationsByOrigin(this.id);
  }

  public getSpellAnimationIds(): string[] {
    return this.spellAnimationIds;
  }

  public canMove(): boolean {
    // moveSpeed = 0 means strictly immobile (parasite phase 1, etc.). Guard
    // needed because move duration is computed via 100/moveSpeed — without it
    // we'd get `Infinity` ticks and the action would never resolve.
    if (this.stats.moveSpeed <= 0) {
      return false;
    }
    if (this.actionState === null) {
      return true;
    }

    const blocking = this.getCurrentBlocking();
    return blocking !== LagType.FULL;
  }

  protected getCurrentBlocking(): LagType {
    if (!this.actionState) {
      return LagType.NONE;
    }

    switch (this.actionState.phase) {
      case ActionPhase.CASTING:
        return this.actionState.timings.castBlocking;
      case ActionPhase.EXECUTING:
        return this.actionState.timings.actionBlocking;
      case ActionPhase.LAGGING:
        return this.actionState.timings.lagBlocking;
      default:
        return LagType.NONE;
    }
  }

  protected isEntityBlocking(x: number, y: number): boolean {
    if (netService.isClient()) {
      // Client move prediction: a remote entity's live positionX/Y is the
      // ~100 ms-delayed interpolated cell, so it can miss one that just entered
      // (x,y) on the host → a predicted step the host rejects (rubber-band).
      // Read the freshest snapshot cell instead.
      const occupantId = dungeonService.freshestOccupantAt(x, y);
      if (occupantId === undefined || occupantId === this.id) {
        return false;
      }
      const occupant = dungeonService.getEntityById(occupantId);
      return occupant !== undefined && !occupant.isWalkableOver;
    }
    const entity = dungeonService.getEntityAt(x, y, this.id);
    return entity !== undefined && !entity.isWalkableOver;
  }

  public clearDirty(): void {
    this.isDirty = false;
  }

  public markDirty(): void {
    this.isDirty = true;
    this.renderVersion++;
  }

  canMoveToPos(x: number, y: number): boolean {
    if ((this as unknown as { noclip?: boolean }).noclip) {
      return true;
    }
    if (isTileBlocking(x, y)) {
      return false;
    }

    if (dungeonService.locationService.isLocationBlockingAt(x, y)) {
      return false;
    }

    if (this.isEntityBlocking(x, y)) {
      return false;
    }

    const deltaX = x - this.positionX;
    const deltaY = y - this.positionY;
    const isDiagonal = deltaX !== 0 && deltaY !== 0;

    if (isDiagonal) {
      // No corner-cutting: both adjacent cardinal tiles must be non-wall.
      const tile1 = mapService.getTileAt(this.positionX, y);
      const tile2 = mapService.getTileAt(x, this.positionY);

      if (tile1?.type === 'wall' || tile2?.type === 'wall') {
        return false;
      }
    }

    return true;
  }

  getRenderClip(): ISpriteClip {
    const config = entitiesConfig[this.configId];
    const animConfig = config?.animations?.[this.currentSpriteName];

    let phaseDuration: number | undefined;
    if (this.actionState) {
      switch (this.actionState.phase) {
        case ActionPhase.CASTING:
          phaseDuration = this.actionState.timings.castTime;
          break;
        case ActionPhase.EXECUTING:
          phaseDuration = this.actionState.timings.actionTime;
          break;
        case ActionPhase.LAGGING:
          phaseDuration = this.actionState.timings.lagTime;
          break;
      }
    }

    const sizeFactor = this.stats.size / 100;
    const baseWidth = animConfig?.width ?? config?.width;
    const baseHeight = animConfig?.height ?? config?.height;

    return {
      spriteName:
        animConfig?.spriteTextureName ??
        `${this.configId}-${this.currentSpriteName}`,
      direction: this.direction,
      width: baseWidth ? baseWidth * sizeFactor : undefined,
      height: baseHeight ? baseHeight * sizeFactor : undefined,
      anchor: (animConfig?.anchor ?? config?.anchor) as
        | [number, number]
        | undefined,
      offsetX: animConfig?.offsetX,
      offsetY: animConfig?.offsetY,
      phaseDuration,
    };
  }

  setPosition(x: number, y: number): void {
    this.positionX = x;
    this.positionY = y;
  }

  /**
   * Hard snap to a cell, logical and visual. Cancels any in-flight visual
   * movement so the next `updateVisualMovement` tick cannot lerp `absoluteX/Y`
   * back toward the old slide target (which would strand the camera on the old
   * position until the next move). cf. teleport effect handlers.
   */
  teleportTo(x: number, y: number): void {
    this.positionX = x;
    this.positionY = y;
    this.absoluteX = x * tileWidth;
    this.absoluteY = y * tileHeight;
    this.visualMovement = null;
    this.markDirty();
  }

  /** Client reconcile: snap the logical cell now but glide the sprite from its
   *  current spot to that cell's grid-aligned pixel over `durationTicks`. Used
   *  instead of `teleportTo` for small per-snapshot corrections so continuous
   *  host-driven motion the client doesn't predict (dash/knockback) reads
   *  smoothly instead of teleporting once per 20 Hz snapshot. Targets the cell
   *  (not the host's raw `ax/ay`, which is mid-slide on the last snapshot) so the
   *  glide always settles ON a cell — no resting half-way once reconcile stops. */
  glideToCell(cellX: number, cellY: number, durationTicks: number): void {
    this.positionX = cellX;
    this.positionY = cellY;
    this.visualMovement = {
      startX: this.absoluteX,
      startY: this.absoluteY,
      targetX: cellX * tileWidth,
      targetY: cellY * tileHeight,
      elapsedTime: 0,
      duration: durationTicks,
    };
    this.markDirty();
  }

  takeDamage(damage: number, crit = false, element?: Element): number {
    if ((this as unknown as { godMode?: boolean }).godMode) {
      return 0;
    }
    if (this.isDead() || this.actionState?.name === 'death') {
      return 0;
    }
    let remaining = damage;
    let totalAbsorbed = 0;

    let shieldAbsorbed = 0;
    if (this.shield > 0) {
      shieldAbsorbed = Math.min(remaining, this.shield);
      this.shield -= shieldAbsorbed;
      remaining -= shieldAbsorbed;
      totalAbsorbed += shieldAbsorbed;
    }

    const lifeAbsorbed = Math.min(remaining, this.life);
    this.life = Math.max(0, this.life - remaining);
    totalAbsorbed += lifeAbsorbed;

    emitEntityEvent(this, 'onDamageTaken');

    const splitDamage = shieldAbsorbed > 0 && lifeAbsorbed > 0;
    if (shieldAbsorbed > 0) {
      this.pushDamageDisplay(
        shieldAbsorbed,
        this.absoluteX + (splitDamage ? -8 : 0),
        this.absoluteY,
        'shieldDamage',
        crit,
        element,
      );
    }
    if (lifeAbsorbed > 0) {
      this.pushDamageDisplay(
        lifeAbsorbed,
        this.absoluteX + (splitDamage ? 8 : 0),
        this.absoluteY,
        'damage',
        crit,
        element,
      );
    }
    if (shieldAbsorbed === 0 && lifeAbsorbed === 0) {
      this.pushDamageDisplay(
        0,
        this.absoluteX,
        this.absoluteY,
        'damage',
        crit,
        element,
      );
    }
    this.lastDamageTakenAt = Date.now();
    this.markDirty();

    if (!isReflecting && totalAbsorbed > 0 && this.lastDamageSourceCasterId) {
      let reflectPct = 0;
      for (const ab of this.activeBuffs) {
        if (ab.def.damageReflectPercent) {
          reflectPct += ab.def.damageReflectPercent;
        }
      }
      if (reflectPct > 0) {
        const reflectAmount = Math.floor(totalAbsorbed * reflectPct);
        if (reflectAmount > 0) {
          const attackerId = this.lastDamageSourceCasterId;
          const attacker = dungeonService
            .getEntities()
            .find((e) => e.id === attackerId);
          if (attacker && !attacker.isDead()) {
            isReflecting = true;
            attacker.takeDamage(reflectAmount);
            isReflecting = false;
          }
        }
      }
    }

    if (this.isDead() && this.actionState?.name !== 'death') {
      emitEntityEvent(this, 'onDeath');
      if (this.isDead()) {
        this.death();
      }
    }

    return totalAbsorbed;
  }

  protected emotionTypesCache: Element[] | null = null;

  /** Overridden by subclasses: player ← equipped sketches, monster ← config. */
  protected computeEmotionTypes(): Element[] {
    return [];
  }

  /** Defensive emotion type(s) of this entity (0-2). Cached. */
  getEmotionTypes(): Element[] {
    if (this.emotionTypesCache === null) {
      this.emotionTypesCache = this.computeEmotionTypes();
    }
    return this.emotionTypesCache;
  }

  /**
   * Emotion damage multiplier: purely the fixed effectiveness chart (cf.
   * data/emotions.config.ts) against the target's emotion type(s), clamped to
   * 0. No elemental stats (resistance/affinity) remain — emotion interaction
   * is entirely type-vs-type.
   */
  public static computeElementalMultiplier(
    target: DungeonEntity,
    element: Element,
  ): number {
    return Math.max(0, emotionEffectiveness(element, target.getEmotionTypes()));
  }

  /**
   * Damage pipeline (cf. docs/stats.md): final stats (already recomputed at
   * tick time) → type-adjusted (division-based) → elemental multiplier → crit
   * → application (shield then life).
   */
  private dealTypedDamage(
    target: DungeonEntity,
    baseDamage: number,
    type: 'physical' | 'magic',
    element?: Element,
  ): number {
    if (!target.targetable) {
      return 0;
    }

    const typeAtk =
      type === 'physical' ? this.stats.physicalAttack : this.stats.magicAttack;
    const typeDef =
      type === 'physical'
        ? target.stats.physicalDefense
        : target.stats.magicDefense;

    // Attack/defense ratio (Atk/Def) softened by an exponent so the stats matter
    // less than a raw ratio: a 2:1 ratio gives ×1.5 and 1:2 → ×0.667. Symmetric,
    // so high defense keeps reducing toward 0 with no floor. Def floored to 1 (no
    // division by zero); attack clamped to ≥ 0 so a malus can't yield NaN from a
    // fractional power of a negative — and zero attack deals zero.
    const ratio = Math.max(0, typeAtk) / Math.max(1, typeDef);
    const statMultiplier = ratio ** STAT_RATIO_EXPONENT;
    let damage = baseDamage * statMultiplier;

    if (element) {
      damage *= DungeonEntity.computeElementalMultiplier(target, element);
    }

    const critChance =
      type === 'physical'
        ? this.stats.critChancePhysical
        : this.stats.critChanceMagic;
    let crit = false;
    if (Math.random() * 100 < critChance) {
      damage *= this.stats.critMultiplier;
      crit = true;
    }

    // Any hit deals at least 1 (the !targetable guard above already returned 0
    // for non-hits, and dodges are resolved before this) — so even a 0 ratio or
    // a tiny fraction still chips 1.
    const applied = Math.max(1, Math.floor(damage));

    const wasAlreadyDead = target.isDead();
    const result = target.takeDamage(applied, crit, element);
    emitEntityEvent(this, 'onAttack');
    if (!wasAlreadyDead && target.isDead()) {
      emitEntityEvent(this, 'onKill', {
        killSourceSpellId: target.lastDamageSourceSpellId,
        eventPosX: target.positionX,
        eventPosY: target.positionY,
      });
    }
    return result;
  }

  public dealPhysicalDamage(
    target: DungeonEntity,
    baseDamage: number,
    element?: Element,
  ): number {
    return this.dealTypedDamage(target, baseDamage, 'physical', element);
  }

  public dealMagicDamage(
    target: DungeonEntity,
    baseDamage: number,
    element?: Element,
  ): number {
    return this.dealTypedDamage(target, baseDamage, 'magic', element);
  }

  death(onAnimationComplete?: () => void): void {
    dungeonService.previewService.removeAffectedTilesByEntity(this.id);
    this.affectedTileIds = [];
    this.currentCastGroupId = null;
    if (netService.isHost()) {
      sendPreviewRemove({ entityId: this.id });
    }
    // Cancel any in-progress slide so the entity dies at its current position
    // instead of finishing its move toward the destination cell.
    this.visualMovement = null;
    const deathDuration = msToTicks(500);

    this.startAction(
      'death',
      false,
      {
        castTime: 0,
        castBlocking: LagType.FULL,
        actionTime: deathDuration,
        actionBlocking: LagType.FULL,
        actionSpriteName: 'die',
        lagTime: 0,
        lagBlocking: LagType.FULL,
      },
      {
        actionProgressCallbacks: [
          {
            percent: 99,
            callback: () => {
              onAnimationComplete?.();
              dungeonService.removeEntity(this.id);
            },
          },
        ],
      },
      true,
    );
  }

  public pushDamageDisplay(
    amount: number,
    worldX: number,
    worldY: number,
    type: 'damage' | 'shieldDamage',
    crit = false,
    element?: Element,
  ): void {
    this.damageDisplays.push({
      id: uuidv4(),
      damage: amount,
      worldX,
      worldY,
      type,
      crit,
      element,
    });
    this.markDirty();
    sendDamageNumber({
      targetId: this.id,
      value: amount,
      worldX,
      worldY,
      display: type,
      crit,
      element,
    });
  }

  public pushHealDisplay(
    amount: number,
    worldX: number,
    worldY: number,
    type: 'healHp' | 'healShield',
  ): void {
    this.healDisplays.push({
      id: uuidv4(),
      heal: amount,
      worldX,
      worldY,
      type,
    });
    this.markDirty();
    sendDamageNumber({
      targetId: this.id,
      value: amount,
      worldX,
      worldY,
      display: type,
    });
  }

  getDamageDisplays(): IDamageDisplay[] {
    return this.damageDisplays;
  }

  removeDamageDisplay(id: string): void {
    this.damageDisplays = this.damageDisplays.filter((d) => d.id !== id);
    this.markDirty();
  }

  getHealDisplays(): IHealDisplay[] {
    return this.healDisplays;
  }

  removeHealDisplay(id: string): void {
    this.healDisplays = this.healDisplays.filter((h) => h.id !== id);
    this.markDirty();
  }

  gainHealth(heal: number): number {
    const actualHeal = Math.min(heal, this.maxLife - this.life);
    this.life = Math.min(this.maxLife, this.life + heal);

    if (actualHeal > 0) {
      this.pushHealDisplay(
        actualHeal,
        this.absoluteX,
        this.absoluteY,
        'healHp',
      );
    }

    return actualHeal;
  }

  gainShield(amount: number): number {
    const actual = Math.min(amount, this.maxShield - this.shield);
    this.shield = Math.min(this.maxShield, this.shield + amount);

    if (actual > 0) {
      this.pushHealDisplay(
        actual,
        this.absoluteX,
        this.absoluteY,
        'healShield',
      );
    }

    return actual;
  }

  /**
   * Cell-by-cell animated knockback, same model as `move()`: each cell is a
   * step that commits the new position at the mid-step collision check, or
   * "bumps" back and applies a collision stun if blocked.
   *
   * `startAction` is called with `force = true` so the knockback preempts any
   * in-progress action — required for an enemy mid-cast to actually be thrown.
   * An already-dead target (`death` action running) is skipped so the death
   * animation and its final `removeEntity` aren't overwritten.
   *
   * @param options.stepMs per-cell animation duration (default KNOCKBACK_STEP_MS).
   * @param options.collisionStunMs stun applied when blocked mid-flight
   *  (default KNOCKBACK_BLOCKED_STUN_MS).
   */
  public applyKnockbackAnimated(
    pushDir: DirectionEnum,
    distance: number,
    options?: { stepMs?: number; collisionStunMs?: number },
  ): void {
    if (!this.knockbackable || distance <= 0) {
      return;
    }
    // Skip if the entity just died: otherwise we'd overwrite the `death`
    // action triggered just before (e.g. by a `dealDamageInCells` preceding
    // `knockbackAll` in sentinel_shockwave's effects), blocking the
    // `removeEntity` in death's 99% callback.
    if (this.isDead() || this.actionState?.name === 'death') {
      return;
    }
    this.cancelCast();
    const stepMs = options?.stepMs ?? KNOCKBACK_STEP_MS;
    const collisionStunMs =
      options?.collisionStunMs ?? KNOCKBACK_BLOCKED_STUN_MS;
    this.runKnockbackStep(pushDir, distance, stepMs, collisionStunMs);
  }

  private knockbackBumped = false;

  private runKnockbackStep(
    pushDir: DirectionEnum,
    remaining: number,
    stepMs: number,
    collisionStunMs: number,
  ): void {
    const target = getTargetPosition(this.positionX, this.positionY, pushDir);
    const originX = this.positionX;
    const originY = this.positionY;
    const stepDuration = diagonalAdjusted(
      msToTicks(stepMs),
      originX,
      originY,
      target.x,
      target.y,
    );

    // Walls and locations are known before the step, but we still translate
    // up to 50% to give the visual bump — consistent with entity collision,
    // where the block is only discovered mid-step.
    const wallBlocked =
      isTileBlocking(target.x, target.y) ||
      dungeonService.locationService.isLocationBlockingAt(target.x, target.y);

    this.knockbackBumped = false;

    this.startAction(
      'knockback',
      false,
      {
        castTime: 0,
        castBlocking: LagType.NONE,
        actionTime: stepDuration,
        actionBlocking: LagType.FULL,
        lagTime: 0,
        lagBlocking: LagType.NONE,
      },
      {
        actionProgressCallbacks: [
          {
            percent: 0,
            callback: () => {
              this.startVisualMovement(target.x, target.y, stepDuration);
            },
          },
          {
            percent: 50,
            callback: () => {
              const entityAt = dungeonService.getEntityAt(
                target.x,
                target.y,
                this.id,
              );
              const blocked = wallBlocked || !!entityAt;
              if (blocked) {
                this.visualMovement = null;
                this.startVisualMovement(originX, originY, stepDuration / 2);
                this.applyStun(msToTicks(collisionStunMs));
                this.knockbackBumped = true;
              } else {
                this.positionX = target.x;
                this.positionY = target.y;
                this.markDirty();
              }
            },
          },
          {
            percent: 100,
            callback: () => {
              if (this.knockbackBumped || remaining <= 1) {
                return;
              }
              // Defer: we're still inside the current action's callback, which
              // will transition to LAGGING (then idle, lagTime=0) once
              // checkProgressCallbacks returns. Starting the next step now
              // would overwrite actionState while that transition tries to
              // advance it → schedule via setTimeout(0).
              setTimeout(() => {
                if (this.isDead() || this.isStunned()) {
                  return;
                }
                this.runKnockbackStep(
                  pushDir,
                  remaining - 1,
                  stepMs,
                  collisionStunMs,
                );
              }, 0);
            },
          },
        ],
      },
      true,
    );
  }

  protected moveBaseMs = 500;

  protected onMoveCommitted(_targetX: number, _targetY: number): void {}

  move(targetX: number, targetY: number): boolean {
    if (!this.canMoveToPos(targetX, targetY)) {
      return false;
    }

    const direction = getDirectionFromPositions(
      this.positionX,
      this.positionY,
      targetX,
      targetY,
    );

    if (!this.canMove()) {
      return false;
    }

    const target = { x: targetX, y: targetY };
    const originX = this.positionX;
    const originY = this.positionY;

    const moveDuration = diagonalAdjusted(
      msToTicks(this.moveBaseMs) * (100 / this.stats.moveSpeed),
      originX,
      originY,
      targetX,
      targetY,
    );

    this.startAction(
      'move',
      false,
      {
        castTime: 0,
        castBlocking: LagType.NONE,
        actionTime: moveDuration,
        actionBlocking: LagType.FULL,
        actionSpriteName: 'run',
        lagTime: 0,
        lagBlocking: LagType.NONE,
      },
      {
        actionProgressCallbacks: [
          {
            percent: 0,
            callback: () => {
              this.direction = direction;
              this.startVisualMovement(target.x, target.y, moveDuration);
            },
          },
          {
            percent: 50,
            callback: () => {
              const entityAtTarget = dungeonService.getEntityAt(
                target.x,
                target.y,
                this.id,
              );

              if (entityAtTarget) {
                this.visualMovement = null;
                this.startVisualMovement(originX, originY, moveDuration / 2);
              } else {
                this.positionX = target.x;
                this.positionY = target.y;
                this.onMoveCommitted(target.x, target.y);
              }
            },
          },
        ],
      },
    );

    return true;
  }

  isDead(): boolean {
    return this.life <= 0;
  }

  getActionState(): IEntityAction | null {
    return this.actionState;
  }

  face(direction: DirectionEnum): void {
    this.direction = direction;
    this.markDirty();
  }
}
